import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LuArrowLeft, LuCheck, LuCreditCard, LuSmartphone } from 'react-icons/lu';
import { TZ_MOBILE_NETWORKS } from '../wakalaConstants';
import { apiJson } from '../apiClient';
import { clearSubscriptionCheckout, readSubscriptionCheckout } from '../utils/subscriptionCheckout';
import { normalizeRole } from '../utils/userLocale';
import './SubscriptionCheckoutPage.css';

function formatTzs(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `Tsh ${n.toLocaleString('en-TZ')}`;
}

const STEPS = [
  { id: 'plan', label: 'Plan' },
  { id: 'pay', label: 'Payment' },
  { id: 'done', label: 'Confirm' },
];

const POLL_MS = 3500;
const POLL_MAX_ATTEMPTS = 40;

export default function SubscriptionCheckoutPage({ user, onNavigate }) {
  const tenant = user?.tenant || {};
  const [plan, setPlan] = useState(() => readSubscriptionCheckout());
  const [method, setMethod] = useState('mobile');
  const [network, setNetwork] = useState('mpesa');
  const [phone, setPhone] = useState(tenant.phone || '');
  const [step, setStep] = useState('pay');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [gateway, setGateway] = useState(null);
  const [waitingPayment, setWaitingPayment] = useState(false);
  const [orderReference, setOrderReference] = useState('');
  const [paymentMessage, setPaymentMessage] = useState('');
  const [paymentFailed, setPaymentFailed] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!plan) onNavigate('subscription');
  }, [plan, onNavigate]);

  useEffect(() => {
    if (normalizeRole(user?.role) !== 'owner') return;
    apiJson('/owner/subscription-billing')
      .then((data) => setGateway(data.paymentGateway || null))
      .catch(() => setGateway(null));
  }, [user?.role]);

  useEffect(() => () => {
    if (pollRef.current) window.clearInterval(pollRef.current);
  }, []);

  const periodLabel = useMemo(() => {
    const end = new Date();
    end.setMonth(end.getMonth() + 1);
    return end.toLocaleDateString('en-TZ', { month: 'short', day: 'numeric', year: 'numeric' });
  }, []);

  if (!plan) return null;

  const networkLabel = TZ_MOBILE_NETWORKS.find((c) => c.value === network)?.label || 'mobile money';
  const clickPesaLive = gateway?.enabled && method === 'mobile';

  const stopPolling = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = (reference) => {
    stopPolling();
    let attempts = 0;
    pollRef.current = window.setInterval(async () => {
      attempts += 1;
      if (attempts > POLL_MAX_ATTEMPTS) {
        stopPolling();
        setWaitingPayment(false);
        setPaymentFailed(true);
        setPaymentMessage('Payment timed out. If you approved on your phone, refresh subscription or contact support.');
        return;
      }
      try {
        const status = await apiJson(
          `/owner/subscription-payments/clickpesa/status/${encodeURIComponent(reference)}`
        );
        if (status.activated) {
          stopPolling();
          setWaitingPayment(false);
          setDone(true);
          setStep('done');
          setPaymentMessage('Payment received. Your subscription is now active.');
        } else if (status.status === 'failed' || status.gatewayStatus === 'FAILED') {
          stopPolling();
          setWaitingPayment(false);
          setPaymentFailed(true);
          setPaymentMessage('Payment failed or was cancelled on your phone. Try again.');
        }
      } catch {
        /* keep polling */
      }
    }, POLL_MS);
  };

  const handleManualRequest = async () => {
    await apiJson('/owner/subscription-requests', {
      method: 'POST',
      body: JSON.stringify({
        planCode: plan.code,
        planName: plan.name,
        amountTzs: plan.priceTzs,
        billingPeriod: plan.billingPeriod || 'monthly',
        method,
        network: method === 'mobile' ? network : null,
        phone: phone.trim() || null,
      }),
    });
    setDone(true);
    setStep('done');
    setPaymentMessage('Request submitted for manual approval.');
  };

  const handleConfirm = async () => {
    if (normalizeRole(user?.role) !== 'owner') {
      return;
    }
    setSubmitting(true);
    setPaymentFailed(false);
    setPaymentMessage('');
    try {
      if (clickPesaLive) {
        const res = await apiJson('/owner/subscription-payments/clickpesa/initiate', {
          method: 'POST',
          body: JSON.stringify({
            planCode: plan.code,
            planName: plan.name,
            amountTzs: plan.priceTzs,
            billingPeriod: plan.billingPeriod || 'monthly',
            network,
            phone: phone.trim(),
          }),
        });
        setOrderReference(res.orderReference || '');
        setWaitingPayment(true);
        setPaymentMessage(
          res.message || 'Check your phone — approve the USSD mobile money prompt to pay.'
        );
        if (res.orderReference) {
          startPolling(res.orderReference);
        }
      } else {
        await handleManualRequest();
      }
    } catch (e) {
      window.alert(e.message || 'Could not start payment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    stopPolling();
    onNavigate('subscription');
  };

  const handleFinish = () => {
    stopPolling();
    clearSubscriptionCheckout();
    onNavigate('subscription');
  };

  return (
    <div className="page-bleed sub-checkout">
      <header className="sub-checkout__hero">
        <button type="button" className="sub-checkout__back" onClick={handleBack}>
          <LuArrowLeft size={18} aria-hidden />
          Back to plans
        </button>
        <span className="sub-checkout__eyebrow">Subscription payment</span>
        <h1 className="sub-checkout__title">Complete payment</h1>
        <p className="sub-checkout__lead">
          {clickPesaLive
            ? `Pay with ${gateway?.label || 'ClickPesa'} — USSD prompt on your ${networkLabel} number.`
            : 'Confirm your plan and payment method. Manual approval is used when the gateway is not configured.'}
        </p>
      </header>

      <ol className="sub-checkout__steps" aria-label="Checkout steps">
        {STEPS.map((s, i) => {
          const active = s.id === step;
          const complete = STEPS.findIndex((x) => x.id === step) > i || (done && s.id !== 'done');
          return (
            <li
              key={s.id}
              className={`sub-checkout__step${active ? ' is-active' : ''}${complete ? ' is-done' : ''}`}
            >
              <span className="sub-checkout__step-num">{complete && !active ? <LuCheck size={14} /> : i + 1}</span>
              <span>{s.label}</span>
            </li>
          );
        })}
      </ol>

      <div className="sub-checkout__layout">
        <aside className="sub-checkout__summary">
          <h2 className="sub-checkout__summary-title">Summary</h2>
          <div className="sub-checkout__summary-card">
            <p className="sub-checkout__biz">{tenant.name || 'Your business'}</p>
            <p className="sub-checkout__plan-name">{plan.name}</p>
            <p className="sub-checkout__price">
              {formatTzs(plan.priceTzs)}
              <span>/ month</span>
            </p>
            <ul className="sub-checkout__highlights">
              {(plan.highlights || []).slice(0, 4).map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>
            <div className="sub-checkout__renew">
              <span>Next period</span>
              <strong>Until {periodLabel}</strong>
            </div>
          </div>
        </aside>

        <section className="sub-checkout__panel">
          {done ? (
            <div className="sub-checkout__success" role="status">
              <div className="sub-checkout__success-icon" aria-hidden>
                <LuCheck size={28} />
              </div>
              <h2>{paymentMessage.includes('active') ? 'Payment successful' : 'Request received'}</h2>
              <p>
                {paymentMessage.includes('active') ? (
                  <>
                    <strong>{plan.name}</strong> ({formatTzs(plan.priceTzs)}/month) is now active on your account.
                  </>
                ) : (
                  <>
                    You selected <strong>{plan.name}</strong> ({formatTzs(plan.priceTzs)}/month). Your request is{' '}
                    <strong>pending approval</strong>.
                    {phone.trim() ? (
                      <>
                        {' '}
                        Payment on <strong>{phone.trim()}</strong>.
                      </>
                    ) : null}
                  </>
                )}
              </p>
              <button type="button" className="sub-checkout__cta" onClick={handleFinish}>
                Back to subscription
              </button>
            </div>
          ) : waitingPayment ? (
            <div className="sub-checkout__waiting" role="status">
              <div className="sub-checkout__waiting-spinner" aria-hidden />
              <h2>Waiting for payment</h2>
              <p>{paymentMessage}</p>
              {orderReference ? (
                <p className="sub-checkout__waiting-ref">
                  Reference: <code>{orderReference}</code>
                </p>
              ) : null}
              <p className="sub-checkout__waiting-hint">
                Approve the prompt on your phone. This page updates automatically when payment is confirmed.
              </p>
            </div>
          ) : (
            <>
              <h2 className="sub-checkout__panel-title">Choose payment method</h2>
              <p className="sub-checkout__panel-sub">
                {clickPesaLive
                  ? 'Mobile money via ClickPesa USSD-PUSH (M-Pesa, Mixx, Airtel, HaloPesa).'
                  : 'All Tanzanian mobile money networks. Card payments are coming soon.'}
              </p>

              <div className="sub-checkout__methods" role="radiogroup" aria-label="Payment type">
                <label className={`sub-checkout__method${method === 'mobile' ? ' is-selected' : ''}`}>
                  <input
                    type="radio"
                    name="pay-method"
                    value="mobile"
                    checked={method === 'mobile'}
                    onChange={() => setMethod('mobile')}
                  />
                  <span className="sub-checkout__method-icon sub-checkout__method-icon--mpesa" aria-hidden>
                    <LuSmartphone size={22} />
                  </span>
                  <span className="sub-checkout__method-text">
                    <strong>Mobile money</strong>
                    <small>M-Pesa, Mixx, Airtel, HaloPesa, and more</small>
                  </span>
                </label>
                <label className="sub-checkout__method sub-checkout__method--disabled">
                  <input type="radio" name="pay-method" value="card" disabled />
                  <span className="sub-checkout__method-icon sub-checkout__method-icon--card" aria-hidden>
                    <LuCreditCard size={22} />
                  </span>
                  <span className="sub-checkout__method-text">
                    <strong>Card</strong>
                    <small>Coming soon</small>
                  </span>
                </label>
              </div>

              {method === 'mobile' ? (
                <>
                  <p className="sub-checkout__networks-label">Choose network</p>
                  <div
                    className="sub-checkout__networks"
                    role="radiogroup"
                    aria-label="Tanzania mobile money networks"
                  >
                    {TZ_MOBILE_NETWORKS.map((ch) => (
                      <label
                        key={ch.value}
                        className={`sub-checkout__network sub-checkout__network--${ch.value}${
                          network === ch.value ? ' is-selected' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="pay-network"
                          value={ch.value}
                          checked={network === ch.value}
                          onChange={() => setNetwork(ch.value)}
                        />
                        <span className="sub-checkout__network-name">{ch.label}</span>
                      </label>
                    ))}
                  </div>
                  <label className="sub-checkout__field">
                    <span>{networkLabel} phone number</span>
                    <input
                      type="tel"
                      placeholder="07xx xxx xxx"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      autoComplete="tel"
                    />
                  </label>
                </>
              ) : null}

              {paymentFailed && paymentMessage ? (
                <div className="sub-checkout__gateway-note sub-checkout__gateway-note--error" role="alert">
                  {paymentMessage}
                </div>
              ) : null}

              <div className="sub-checkout__gateway-note">
                {clickPesaLive ? (
                  <>
                    <strong>ClickPesa</strong> will send a USSD prompt to your phone. Approve to activate instantly.
                  </>
                ) : (
                  <>
                    <strong>Payment gateway</strong> not configured — click confirm to submit for manual approval.
                  </>
                )}
              </div>

              <button
                type="button"
                className="sub-checkout__cta"
                disabled={submitting || (method === 'mobile' && !phone.trim())}
                onClick={() => void handleConfirm()}
              >
                {submitting
                  ? 'Please wait…'
                  : clickPesaLive
                    ? `Pay ${formatTzs(plan.priceTzs)}`
                    : `Confirm — ${formatTzs(plan.priceTzs)}`}
              </button>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
